import PrimaryButton from "components/core/PrimaryButton";
import React, { ChangeEvent, FormEvent, Fragment, useCallback, useState } from "react";
import { apiAuth } from "services";
import { AxiosError } from "axios";
import { IErrorData } from "services/types";
import { TextInput } from "components/inputs/TextInput/TextInput";
import authPopupClasses from "./../AuthPopup.module.scss";

interface IProps {
  onClose: () => void;
}

const emailRegExp = new RegExp(/^[A-Za-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}/);

const ResetPassword = ({ onClose }: IProps) => {
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>("");

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    validateEmail(e.target.value);
  };

  const validateEmail = (value: string) => {
    let error = null;

    if (value.length === 0) {
      error = "Please specify your email address.";
      setEmailError(error);
      return;
    }

    if (!emailRegExp.test(value)) {
      error = "Invalid email";
      setEmailError(error);
      return;
    }

    setEmailError(error);
  };

  const isValid = useCallback(
    () => emailError === null,
    [emailError]
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    validateEmail(email);

    if (!isValid()) {
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSending(true);

    try {
      const { message } = await apiAuth.resetPassword({ email });
      setSuccessMessage(message);
    } catch (e) {
      const message = (e as AxiosError<IErrorData>)?.response?.data?.error?.message || "Service unavailable";
      setErrorMessage(message);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className={authPopupClasses.ContentContainer}>
          <TextInput
            label="Email"
            onChange={handleChangeEmail}
            value={email}
            error={emailError === null ? undefined : emailError}
          />

          { errorMessage &&
              <div className={authPopupClasses.ErrorMessage}>
                { errorMessage }
              </div>
          }

          { successMessage === null && (
            <PrimaryButton
              fullWidth
              type="submit"
              disabled={!isValid() || isSending}
            >Send</PrimaryButton>
          )}

          { successMessage && (
            <Fragment>
              <div className={authPopupClasses.SuccessMessage}>
                { successMessage }
              </div>

              <PrimaryButton
                fullWidth
                type="button"
                onClick={handleClose}
              >
                Ok
              </PrimaryButton>
            </Fragment>
          )}
        </div>
      </form>
    </div>
  );
};

export default ResetPassword;
